import React, { useEffect, useState, useCallback } from "react";
import { Sun, Moon, Calendar } from 'lucide-react';
import apiClient from '../utils/apiClient';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import EventCard from "../components/EventCard";
import EventsHero from "../components/EventsHero";
import EventDetailModal from "../components/EventDetailModal";
import dayjs from "dayjs";

/**
 * EventsPage - Card-based golf events listing
 *
 * Design Philosophy:
 * - Card grid layout (similar to golfstat.com live scoring)
 * - Light mode default for outdoor viewing
 * - Dark mode toggle for evening use
 * - Event categories: Upcoming, Past FedUp, Past Non-FedUp
 * - Click card to open detail modal
 */
const EventsPage = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastFedUpEvents, setPastFedUpEvents] = useState([]);
  const [pastNonFedUpEvents, setPastNonFedUpEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize dark mode from localStorage/system preference
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    if (stored) {
      setIsDarkMode(stored === 'dark');
    } else {
      try {
        const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
        if (mediaQuery?.matches) {
          setIsDarkMode(true);
        }
      } catch {
        // Fallback for test environments
      }
    }
  }, []);

  // Sync dark mode to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Fetch events function (memoized for use in callbacks)
  const fetchEvents = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const response = await apiClient.get('/admin/events', {
        params: { limit: 500 }
      });

      const payload = response.data?.events ? response.data : response;
      const eventsData = payload.events || payload || [];

      const upcoming = eventsData.filter((event) => !event.closed);
      const pastFedUp = eventsData.filter((event) => event.closed && event.fedup_eligible);
      const pastNonFedUp = eventsData.filter((event) => event.closed && !event.fedup_eligible);

      // Sort upcoming by date ascending (nearest first)
      const sortedUpcoming = [...upcoming].sort((a, b) =>
        dayjs(a.date).valueOf() - dayjs(b.date).valueOf()
      );

      // Sort past by date descending (most recent first)
      const sortedPastFedUp = [...pastFedUp].sort((a, b) =>
        dayjs(b.date).valueOf() - dayjs(a.date).valueOf()
      );

      const sortedPastNonFedUp = [...pastNonFedUp].sort((a, b) =>
        dayjs(b.date).valueOf() - dayjs(a.date).valueOf()
      );

      setUpcomingEvents(sortedUpcoming);
      setPastFedUpEvents(sortedPastFedUp);
      setPastNonFedUpEvents(sortedPastNonFedUp);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Get events for current tab
  const getCurrentEvents = () => {
    switch (activeTab) {
      case 'upcoming':
        return upcomingEvents;
      case 'past':
        return pastFedUpEvents;
      case 'non-fedup':
        return pastNonFedUpEvents;
      default:
        return upcomingEvents;
    }
  };

  const currentEvents = getCurrentEvents();

  // Handle event card click
  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setSelectedEvent(null);
  };

  // Tab configuration
  const tabs = [
    { id: 'upcoming', label: 'Upcoming', count: upcomingEvents.length, color: 'forest' },
    { id: 'past', label: 'Past FedUp', count: pastFedUpEvents.length, color: 'gray' },
    { id: 'non-fedup', label: 'Non-FedUp', count: pastNonFedUpEvents.length, color: 'gray' },
  ];

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-navy-950 flex flex-col transition-colors duration-300">
      <Navbar />

      <main className="flex-grow">
        {/* Hero Header Section */}
        <div className="bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-navy-800">
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-6">
                <div>
                  <h1 className="text-3xl md:text-4xl font-display font-bold text-forest-900 dark:text-white tracking-tight">
                    Events
                  </h1>
                  <p className="text-gray-600 dark:text-navy-400 mt-1">
                    Golf League Schedule
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Dark Mode Toggle */}
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="
                    flex items-center gap-2 px-4 py-2 rounded-full
                    bg-gray-100 dark:bg-navy-800
                    text-gray-700 dark:text-gray-200
                    hover:bg-gray-200 dark:hover:bg-navy-700
                    transition-colors duration-200
                    border border-gray-200 dark:border-navy-700
                  "
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? (
                    <>
                      <Sun className="w-5 h-5 text-gold-400" />
                      <span className="text-sm font-medium hidden sm:inline">Light</span>
                    </>
                  ) : (
                    <>
                      <Moon className="w-5 h-5 text-navy-600" />
                      <span className="text-sm font-medium hidden sm:inline">Dark</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Events Hero - Next Event Spotlight */}
            <EventsHero
              upcomingEvents={upcomingEvents}
              pastEvents={pastFedUpEvents}
              onEventClick={handleEventClick}
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Tab Navigation */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm
                  transition-all duration-200 border
                  ${activeTab === tab.id
                    ? tab.id === 'upcoming'
                      ? 'bg-forest-600 text-white border-forest-600 shadow-md'
                      : 'bg-gray-700 text-white border-gray-700 shadow-md'
                    : 'bg-white dark:bg-navy-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-navy-700 hover:border-forest-300 dark:hover:border-forest-600'
                  }
                `}
              >
                <Calendar className="w-4 h-4" />
                {tab.label}
                <span className={`
                  px-2 py-0.5 rounded-full text-xs font-bold
                  ${activeTab === tab.id
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 dark:bg-navy-700 text-gray-600 dark:text-gray-300'
                  }
                `}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-forest-500 border-t-transparent" />
              <span className="sr-only">Loading events...</span>
            </div>
          ) : (
            <>
              {/* Event Cards Grid */}
              {currentEvents.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                  <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">
                    No {activeTab === 'upcoming' ? 'upcoming' : activeTab === 'past' ? 'past FedUp' : 'non-FedUp'} events
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {currentEvents.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      signupCount={event.signup_count || 0}
                      isUpcoming={activeTab === 'upcoming'}
                      onClick={() => handleEventClick(event)}
                      isSelected={selectedEvent?.id === event.id}
                    />
                  ))}
                </div>
              )}

              {/* Event Count */}
              {currentEvents.length > 0 && (
                <div className="mt-6 text-center text-sm text-gray-500 dark:text-navy-400">
                  Showing {currentEvents.length} event{currentEvents.length !== 1 ? 's' : ''}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Event Detail Modal */}
      <EventDetailModal
        event={selectedEvent}
        isOpen={!!selectedEvent}
        onClose={handleCloseModal}
        upcomingEvents={upcomingEvents}
        onEventUpdate={() => fetchEvents(false)}
      />

      <Footer />
    </div>
  );
};

export default EventsPage;
